'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { Input } from '@/ui/input'
import { searchHandle } from './actions'
import { SpinnerGapIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'

interface HandleSearchResult {
  userId: string
  handle: string
}

interface HandleSearchProps {
  onSelect: (result: HandleSearchResult) => void
  disabled?: boolean
}

export function HandleSearch({ onSelect, disabled }: HandleSearchProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<HandleSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (value.trim().length < 2) {
      setResults([])
      setShowDropdown(false)
      setLoading(false)
      return
    }

    setLoading(true)
    debounceRef.current = setTimeout(async () => {
      const data = await searchHandle(value.trim())
      setResults(data)
      setShowDropdown(true)
      setLoading(false)
    }, 300)
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MagnifyingGlassIcon className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder="Search by handle..."
          className="pl-9"
          disabled={disabled}
        />
        {loading && (
          <SpinnerGapIcon className="text-muted-foreground absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 animate-spin" />
        )}
      </div>

      {showDropdown && (
        <div className="bg-popover absolute top-full z-50 mt-1 w-full rounded-md border shadow-md">
          {results.length === 0 ? (
            <p className="text-muted-foreground px-3 py-2 text-sm">
              No users found
            </p>
          ) : (
            results.map((r) => (
              <button
                key={r.userId}
                type="button"
                className="hover:bg-accent w-full px-3 py-2 text-left text-sm transition-colors"
                onClick={() => {
                  onSelect(r)
                  setQuery('')
                  setShowDropdown(false)
                }}
              >
                @{r.handle}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
